import React, { useEffect, useMemo, useState } from "react";
import { fetchAuthSession } from "@aws-amplify/auth";
import MainLayout from "../../../components/layouts/MainLayout/MainLayout";
import env from "../../../config/env";

const DEFAULT_WALL_BASE = "https://nbv4ejg6u4.execute-api.us-east-1.amazonaws.com/Prod/wall";
const getWallBaseUrl = () => {
  //const base = env.WALL_API_URL?.replace(/\/$/, "") || env.API_URL?.replace(/\/$/, "") || DEFAULT_WALL_BASE;
  //const hasWallSegment = /(?:^|\/)wall(?:$|\/|\?)/i.test(base);
  return DEFAULT_WALL_BASE;
};

const buildHeaders = () => {
  const headers = {};
  if (env.API_KEY) headers["x-api-key"] = env.API_KEY;
  return headers;
};

async function getAuthHeader() {
  try {
    const tokens = (await fetchAuthSession()).tokens;
    const idToken = tokens?.idToken?.toString();
    if (idToken) return { Authorization: `Bearer ${idToken}` };
  } catch (error) {
    console.error("Toolbox.getAuthHeader: unable to fetch session", error);
  }
  return {};
}

async function fetchWithAuth(input, init = {}) {
  const authHeader = await getAuthHeader();
  const headers = {
    ...(init.body ? { "Content-Type": "application/json" } : {}),
    ...buildHeaders(),
    ...authHeader,
    ...(init.headers || {})
  };
  const res = await fetch(input, { ...init, headers, mode: "cors", cache: "no-cache" });
  if (res.status === 204) return null;
  return res.json();
}

async function listFavorites() {
  const base = getWallBaseUrl();
  return fetchWithAuth(`${base}/favorites`, { method: "GET" });
}

async function getProfile() {
  const base = getWallBaseUrl();
  return fetchWithAuth(`${base}/user/profile`, { method: "GET" });
}

async function toggleFavorite({ postId, action = "remove" }) {
  const base = getWallBaseUrl();
  return fetchWithAuth(`${base}/posts/${postId}/favorite`, {
    method: "POST",
    body: JSON.stringify({ action })
  });
}

const formatDate = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  const now = new Date();
  const sameDay = d.toDateString() === now.toDateString();
  return sameDay
    ? `Guardado hoy · ${d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}`
    : `Guardado el ${d.toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" })}`;
};

const deriveTitle = (item) => {
  if (item.customTitle) return item.customTitle;
  const txt = (item.content || "").trim();
  if (!txt) return "Sin título";
  return txt.length > 80 ? `${txt.slice(0, 77)}…` : txt;
};

const normalizeToolItem = (fav) => {
  const snap = fav.snapshot || {};
  const tags = Array.isArray(snap.topics) ? snap.topics : [];
  return {
    id: fav.id || fav.post_id || Math.random().toString(36).slice(2),
    createdAt: snap.created_at || fav.created_at || "",
    authorName: snap.user_name || "Usuario",
    content: snap.text || "",
    originalPostId: fav.post_id,
    originalPostUrl: null, // ASSUMPTION: no URL pública expuesta
    customTitle: snap.custom_title,
    type: snap.post_type || "otro",
    tags,
    isFavorite: true,
    userAvatar: snap.user_avatar_url || null,
  };
};

const FILTERS = [
  { key: "all", label: "Todos" },
  { key: "frase", label: "Frases" },
  { key: "logro", label: "Logros" },
  { key: "ejercicio", label: "Ejercicios" },
  { key: "favoritos", label: "Favoritos" },
];

const SORTS = [
  { key: "recent", label: "Más recientes" },
  { key: "oldest", label: "Más antiguos" },
  { key: "fav", label: "Favoritos primero" },
];

const Toolbox = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState("");
  const [filter, setFilter] = useState("all");
  const [sort, setSort] = useState("recent");
  const [pendingDelete, setPendingDelete] = useState(null);
  const [profile, setProfile] = useState({});

  const loadFavorites = async () => {
    setError(null);
    try {
      setLoading(true);
      const data = await listFavorites();
      const normalized = (data.items || []).map(normalizeToolItem);
      setItems(normalized);
    } catch (e) {
      console.error(e);
      setError("No se pudo cargar tu caja de herramientas");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const init = async () => {
      await loadFavorites();
      try {
        const p = await getProfile();
        setProfile(p || {});
      } catch (e) {
        console.error("Toolbox.getProfile error", e);
      }
    };
    init();
  }, []);

  const handleRemove = async (postId) => {
    try {
      await toggleFavorite({ postId, action: "remove" });
      setItems((prev) => prev.filter((f) => f.originalPostId !== postId));
    } catch (e) {
      console.error(e);
      alert("No se pudo quitar de favoritos");
    }
  };

  const handleToggleFavorite = async (id) => {
    setItems((prev) =>
      prev.map((it) => (it.id === id ? { ...it, isFavorite: !it.isFavorite } : it))
    );
    // TODO: persistir favorito en backend (no hay endpoint dedicado en toolbox)
  };

  const filtered = useMemo(() => {
    const term = search.toLowerCase().trim();
    return items
      .filter((it) => {
        if (filter === "favoritos" && !it.isFavorite) return false;
        if (["frase", "logro", "ejercicio"].includes(filter) && it.type !== filter) return false;
        if (!term) return true;
        const haystack = `${it.customTitle || ""} ${it.content || ""} ${it.tags?.join(" ") || ""}`.toLowerCase();
        return haystack.includes(term);
      })
      .sort((a, b) => {
        if (sort === "fav") {
          if (a.isFavorite && !b.isFavorite) return -1;
          if (!a.isFavorite && b.isFavorite) return 1;
        }
        const da = new Date(a.createdAt || 0).getTime();
        const db = new Date(b.createdAt || 0).getTime();
        if (sort === "oldest") return da - db;
        return db - da;
      });
  }, [items, search, filter, sort]);

  return (
    <MainLayout>
      <div className="flex flex-col" style={{ minHeight: "calc(100vh - 74px)" }}>
        <main className="flex-1 p-6 md:p-8 overflow-y-auto space-y-4" style={{ backgroundColor: "#F3F3F3" }}>
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold mb-1">Mi caja de herramientas</h1>
              <p className="text-sm text-gray-600">
                Aquí guardas las frases, ejercicios y mensajes que te hacen bien. Vuelve cuando necesites recordarte que ya has podido antes.
              </p>
            </div>
            <button
              className="px-3 py-2 rounded-lg border border-gray-300 bg-white hover:bg-gray-100 text-sm"
              onClick={loadFavorites}
              disabled={loading}
            >
              Recargar
            </button>
          </div>

          {error && (
            <div className="bg-amber-50 border border-amber-200 text-amber-800 px-4 py-3 rounded-xl">{error}</div>
          )}

          {loading && <p className="text-gray-600">Cargando tus herramientas…</p>}

          {!loading && filtered.length === 0 && (
            <div className="bg-white rounded-2xl border border-gray-200 p-6 text-center text-gray-600 space-y-2">
              <div className="text-3xl">🧰</div>
              <p className="font-semibold">Todavía no guardas nada en tu caja de herramientas.</p>
              <p className="text-sm">
                Cuando veas un post que te inspire, toca “Guardar en mi caja de herramientas” y aparecerá aquí.
              </p>
            </div>
          )}

          <ul className="space-y-3">
            {filtered.map((item) => (
              <li key={item.id} className="bg-white rounded-2xl border border-gray-200 p-4 shadow-sm">
                <div className="flex items-start justify-between gap-3">
                  <div className="flex-1 space-y-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <h3 className="font-semibold text-gray-900">{deriveTitle(item)}</h3>
                      {item.type && (
                        <span className="px-2 py-1 text-xs rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200">
                          {item.type}
                        </span>
                      )}
                      {item.tags?.map((t) => (
                        <span key={t} className="px-2 py-1 text-xs rounded-full bg-gray-100 text-gray-600 border border-gray-200">
                          #{t}
                        </span>
                      ))}
                    </div>
                    <div className="flex items-center gap-2 text-xs text-gray-500">
                      {(item.userAvatar || profile.avatar_url) && (
                        <img
                          src={item.userAvatar || profile.avatar_url}
                          alt={item.authorName || "Autor"}
                          className="w-8 h-8 rounded-full object-cover"
                        />
                      )}
                      <span>
                        {item.authorName} · {formatDate(item.createdAt)}
                      </span>
                    </div>
                    <p className="text-gray-800 whitespace-pre-wrap leading-relaxed">{item.content}</p>
                    {item.originalPostUrl && (
                      <a
                        href={item.originalPostUrl}
                        className="text-indigo-600 text-sm hover:underline"
                        target="_blank"
                        rel="noreferrer"
                      >
                        Ver publicación completa
                      </a>
                    )}
                  </div>
                  <div className="flex flex-col items-end gap-2">
                    <button
                      className={`text-xl ${item.isFavorite ? "text-amber-500" : "text-gray-400 hover:text-gray-600"}`}
                      onClick={() => handleToggleFavorite(item.id)}
                      title={item.isFavorite ? "Quitar de favoritos" : "Marcar como favorito"}
                    >
                      {item.isFavorite ? "★" : "☆"}
                    </button>
                    <button
                      className="text-red-500 hover:text-red-600 text-sm"
                      onClick={() => setPendingDelete(item)}
                    >
                      Eliminar
                    </button>
                  </div>
                </div>
              </li>
            ))}
          </ul>
        </main>
      </div>

      {/* Modal de confirmación */}
      {pendingDelete && (
        <div className="fixed inset-0 z-50 flex items-center justify-center px-4">
          <div className="absolute inset-0 bg-black/40" onClick={() => setPendingDelete(null)} />
          <div className="relative bg-white rounded-2xl shadow-xl max-w-sm w-full p-6 space-y-4">
            <div className="space-y-2">
              <h3 className="text-lg font-semibold text-gray-900">Eliminar de tu caja</h3>
              <p className="text-sm text-gray-600">
                ¿Quieres eliminar “{deriveTitle(pendingDelete)}” de tu caja de herramientas? Podrás volver a guardarlo si lo encuentras de nuevo.
              </p>
            </div>
            <div className="flex justify-end gap-2">
              <button
                className="px-4 py-2 rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-100"
                onClick={() => setPendingDelete(null)}
              >
                Cancelar
              </button>
              <button
                className="px-4 py-2 rounded-lg bg-red-500 text-white hover:bg-red-600"
                onClick={async () => {
                  const postId = pendingDelete.originalPostId;
                  setPendingDelete(null);
                  await handleRemove(postId);
                }}
              >
                Eliminar
              </button>
            </div>
          </div>
        </div>
      )}
    </MainLayout>
  );
};

export default Toolbox;
